import { useState } from "react";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";

import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { StripePaymentElementChangeEvent } from "@stripe/stripe-js";

import { Button } from "@covertree/ui";
import { PaymentMethodsStripeUpdateDisclaimer } from "../PaymentMethodsCardUpdateDisclaimer";
import { SubmitStripePaymentMethodParams } from "../useAddNewStripePaymentMethod";

interface Props {
  isLoading?: boolean;
  onCancel(): void;
  onSubmit(params: SubmitStripePaymentMethodParams): void;
  shouldTriggerRenewalReissuance: boolean;
  renewalLocator?: string;
}

export function NewPaymentMethodForm({
  isLoading,
  onCancel,
  onSubmit,
  shouldTriggerRenewalReissuance,
  renewalLocator,
}: Props) {
  const stripe = useStripe();
  const elements = useElements();

  const [stripeElementState, setStripeElementState] =
    useState<StripePaymentElementChangeEvent>();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    onSubmit({ stripe, elements });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <Card>
          <CardContent>
            <PaymentElement
              options={{
                layout: "tabs",
                business: { name: "CoverTree" },
                readOnly: isLoading,
                fields: {
                  billingDetails: {
                    address: "never",
                    email: "never",
                    name: "never",
                  },
                },
                terms: {
                  card: "never",
                  usBankAccount: "never",
                },
                paymentMethodOrder: ["card", "us_bank_account"],
              }}
              onChange={(e) => setStripeElementState(e)}
            />
          </CardContent>
        </Card>
        {shouldTriggerRenewalReissuance && (
          <PaymentMethodsStripeUpdateDisclaimer
            renewalLocator={renewalLocator}
          />
        )}
        <Stack mt={3} direction="row" justifyContent="flex-start" spacing={1}>
          <Button
            loading={isLoading}
            disabled={isLoading || !stripeElementState?.complete}
            size="large"
            type="submit">
            Add Payment Method
          </Button>

          <Button
            variant="outlined"
            disabled={isLoading}
            size="large"
            onClick={onCancel}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}
